"use client";

import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { NotionPartner } from "@/lib/notion/types/partnersTypes";
import { Card, CardContent } from "@packages/ui/src/components/shadcn/card";
import { ContainerGlob } from "../../../../componentsGLob/ContainerGlob";

export default function PartnersList() {
  const { t } = useTranslation("buzzCard");
  const [partners, setPartners] = useState<NotionPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/partners");
      if (!res.ok) throw new Error(t("partnersSection.error"));
      const json = await res.json();
      if (!json.success)
        throw new Error(json.message || t("partnersSection.error"));
      setPartners(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("partnersSection.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <ContainerGlob>
        <div
          className="flex justify-center items-center py-12"
          aria-live="polite"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="sr-only">{t("partnersSection.loading")}</span>
        </div>
      </ContainerGlob>
    );
  }

  if (error) {
    return (
      <ContainerGlob>
        <div className="text-center py-12" role="alert">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t("partnersSection.retry")}
          </button>
        </div>
      </ContainerGlob>
    );
  }

  if (partners.length === 0) {
    return (
      <ContainerGlob>
        <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500">
          <p className="text-2xl font-semibold mb-4">
            {t("partnersSection.emptyTitle")}
          </p>
          <p>{t("partnersSection.emptyDescription")}</p>
        </div>
      </ContainerGlob>
    );
  }

  return (
    <section className="py-16 md:py-20" id="partners">
      <ContainerGlob>
        <h2 className="text-3xl font-bold mb-12 text-center">
          {t("partnersSection.sectionTitle")}
        </h2>

        <div className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {partners.map((partner) => (
            <Card
              key={partner.id}
              className="flex flex-col items-center justify-center border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <h3 className="text-lg font-semibold mb-3 truncate max-w-full">
                  {partner.accountName || t("partnersSection.noName")}
                </h3>
              </CardContent>
            </Card>
          ))}
        </div>
      </ContainerGlob>
    </section>
  );
}
